import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  ArrowRight,
  Archive,
  Share2,
  Pencil,
  Trash2,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import AppHeader from "../components/app/AppHeader";
import TemplateProviderDialog from "../components/app/TemplateProviderDialog";
import SharedRenameDialog from "../components/common/RenameDialog";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const TIER_COLORS = {
  frontend: "var(--tier-frontend)",
  compute: "var(--tier-compute)",
  data: "var(--tier-data)",
  networking: "var(--tier-networking)",
  security: "var(--tier-security)",
};

const PROVIDER_META = {
  azure: { code: "AZ", color: "#0EA5E9" },
  aws: { code: "AW", color: "#F59E0B" },
  gcp: { code: "GC", color: "#22C55E" },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

function TierPreview({ architecture, app_type, scale_tier }) {
  const tiers = ["frontend", "compute", "data", "networking", "security"];
  const present =
    Array.isArray(architecture) && architecture.length
      ? Array.from(new Set(architecture.map((c) => c.tier).filter(Boolean)))
      : ["frontend", "compute", "data"];

  // Approximate node counts per tier so the preview hints at composition
  const counts = (architecture || []).reduce((acc, n) => {
    if (n?.tier) acc[n.tier] = (acc[n.tier] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      className="w-full rounded-[8px] border flex items-center"
      style={{
        height: 80,
        background:
          "linear-gradient(180deg, rgba(91,91,214,0.05) 0%, rgba(255,255,255,0) 100%)",
        borderColor: "var(--color-border-secondary)",
        padding: "0 16px",
        gap: 14,
        overflow: "hidden",
      }}
      data-testid="project-tier-preview"
    >
      {tiers.map((t) => {
        const active = present.includes(t);
        const n = counts[t] || 0;
        return (
          <div
            key={t}
            className="flex flex-col items-center"
            style={{ opacity: active ? 1 : 0.25, gap: 6 }}
            title={`${t}${n ? ` · ${n} node${n > 1 ? "s" : ""}` : ""}`}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 9999,
                background: TIER_COLORS[t],
                display: "inline-block",
                boxShadow: active ? `0 0 0 3px ${TIER_COLORS[t]}22` : "none",
              }}
            />
            <span
              className="font-mono"
              style={{
                fontSize: 9,
                color: "var(--color-text-tertiary)",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {t.slice(0, 4)}
            </span>
          </div>
        );
      })}
      <span
        className="font-mono ml-auto"
        style={{
          fontSize: 10,
          color: "var(--color-text-tertiary)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {(architecture || []).length} {architecture && architecture.length === 1 ? "service" : "services"}
      </span>
    </div>
  );
}

function ProviderPill({ provider }) {
  const meta = PROVIDER_META[provider] || PROVIDER_META.azure;
  return (
    <span
      className="inline-flex items-center font-mono rounded-full"
      style={{
        padding: "2px 8px",
        fontSize: 10,
        letterSpacing: "0.08em",
        color: meta.color,
        background: `${meta.color}1A`,
        border: `1px solid ${meta.color}66`,
      }}
      data-testid="project-provider-pill"
    >
      {meta.code}
    </span>
  );
}

function CardAction({ label, icon, onClick, testId, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="grid place-items-center rounded-[6px] bg-bg-tertiary border border-border-primary motion-micro"
      style={{ width: 28, height: 28 }}
      aria-label={label}
      title={label}
      onMouseEnter={(e) =>
        (e.currentTarget.style.borderColor = danger
          ? "var(--color-danger)"
          : "var(--color-accent)")
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.borderColor = "var(--color-border-primary)")
      }
    >
      {icon}
    </button>
  );
}

function ProjectCard({ project, onOpen, onShare, onRename, onArchive, onDelete }) {
  return (
    <div
      className="group relative card-lift bg-bg-secondary border border-border-primary rounded-[12px] p-4 flex flex-col gap-4"
      data-testid={`project-card-${project.id}`}
    >
      {/* Whole-card opener. It MUST come before the per-action buttons: both are
          absolutely positioned in the same stacking context, so when this sat
          last in the DOM it painted on top and swallowed every click on Share /
          Rename / Delete — you always got "open the architecture" instead. */}
      <button
        onClick={() => onOpen(project)}
        aria-label={`Open ${project.name}`}
        className="absolute inset-0 opacity-0"
        data-testid={`project-card-open-${project.id}`}
      />

      <TierPreview
        architecture={project.architecture}
        app_type={project.app_type}
        scale_tier={project.scale_tier}
      />

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3
            className="text-text-primary truncate"
            style={{ fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em" }}
            data-testid="project-name"
            title={project.name}
          >
            {project.name}
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span
              className="font-mono rounded-full"
              style={{
                padding: "2px 8px",
                fontSize: 10,
                letterSpacing: "0.06em",
                color: "var(--color-text-secondary)",
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-secondary)",
                textTransform: "uppercase",
              }}
            >
              {project.app_type}
            </span>
            <span
              className="font-mono rounded-full"
              style={{
                padding: "2px 8px",
                fontSize: 10,
                letterSpacing: "0.06em",
                color: "var(--color-text-secondary)",
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-secondary)",
                textTransform: "uppercase",
              }}
            >
              {project.scale_tier}
            </span>
          </div>
        </div>
        <ProviderPill provider={project.selected_provider} />
      </div>

      <div className="flex items-center justify-between mt-1">
        <span
          className="font-mono text-text-tertiary"
          style={{ fontSize: 11, letterSpacing: "0.02em" }}
        >
          Updated {timeAgo(project.updated_at)}
        </span>
      </div>

      {/* Hover actions. Revealed on hover but also on keyboard focus, so they're
          reachable without a mouse. */}
      <div
        className="absolute top-3 right-3 flex items-center gap-1 motion-micro opacity-0 group-hover:opacity-100 focus-within:opacity-100"
        data-testid={`project-actions-${project.id}`}
      >
        <CardAction
          label="Share"
          testId={`project-share-${project.id}`}
          onClick={() => onShare(project)}
          icon={<Share2 size={14} color="var(--color-text-secondary)" />}
        />
        <CardAction
          label="Rename"
          testId={`project-rename-${project.id}`}
          onClick={() => onRename(project)}
          icon={<Pencil size={14} color="var(--color-text-secondary)" />}
        />
        <CardAction
          label="Archive"
          testId={`project-archive-${project.id}`}
          onClick={() => onArchive(project)}
          icon={<Archive size={14} color="var(--color-text-secondary)" />}
        />
        <CardAction
          label="Delete"
          testId={`project-delete-${project.id}`}
          onClick={() => onDelete(project)}
          danger
          icon={<Trash2 size={14} color="var(--color-text-secondary)" />}
        />
      </div>
    </div>
  );
}

const STARTER_TEMPLATES = [
  {
    id: "rest-api",
    title: "REST API Starter",
    desc: "Three-tier REST API starter for review and customization.",
  },
  {
    id: "ecommerce",
    title: "E-Commerce Stack",
    desc: "Scalable store with search and inventory.",
  },
  {
    id: "serverless-pipeline",
    title: "Serverless Pipeline",
    desc: "Event-driven data processing workflow.",
  },
];

function WelcomeState({ onCreate, onUseTemplate }) {
  return (
    <div className="relative pt-8 pb-16 fade-up" data-testid="welcome-empty-state">
      {/* Soft radial glow — the hero spotlight */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          left: "50%",
          top: 240,
          transform: "translate(-50%, -50%)",
          width: 1200,
          height: 760,
          background:
            "radial-gradient(closest-side, color-mix(in oklab, var(--color-accent) 22%, transparent), transparent 70%)",
          filter: "blur(12px)",
          zIndex: 0,
        }}
      />

      <div
        className="relative text-center mx-auto"
        style={{ maxWidth: 760, zIndex: 1 }}
      >
        <h1
          className="text-text-primary"
          style={{
            fontSize: "clamp(40px, 5.4vw, 60px)",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.05,
          }}
          data-testid="welcome-headline"
        >
          Welcome to Guided Sentry
        </h1>
        <p
          className="text-text-secondary mt-6 mx-auto"
          style={{
            fontSize: 17,
            lineHeight: 1.55,
            maxWidth: 520,
          }}
        >
          Describe what you&apos;re building and we&apos;ll generate your cloud architecture.
        </p>

        <div className="mt-10 flex justify-center">
          <button
            onClick={onCreate}
            data-testid="welcome-create-cta"
            className="motion-canvas inline-flex items-center justify-center gap-2"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
              height: 56,
              padding: "0 32px",
              borderRadius: 9999,
              fontSize: 16,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
              boxShadow:
                "0 1px 0 rgba(255,255,255,0.18) inset, 0 24px 56px -20px color-mix(in oklab, var(--color-accent) 65%, transparent)",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-accent-hover)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-accent)")}
          >
            <Plus size={18} strokeWidth={2.5} />
            Create Your First Project
          </button>
        </div>
      </div>

      {/* Templates row */}
      <div className="relative mt-24" style={{ zIndex: 1 }}>
        <p
          className="text-center text-text-tertiary"
          style={{ fontSize: 13, lineHeight: 1.55 }}
        >
          Not sure where to start? Try a template.
        </p>

        <div
          className="mt-8 grid gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}
          data-testid="starter-template-grid"
        >
          {STARTER_TEMPLATES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onUseTemplate(t)}
              data-testid={`template-card-${t.id}`}
              className="group text-left rounded-[12px] border motion-canvas card-lift"
              style={{
                background: "var(--color-background-secondary)",
                borderColor: "var(--color-border-primary)",
                padding: 24,
                cursor: "pointer",
              }}
            >
              <h3
                className="text-text-primary"
                style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.005em" }}
              >
                {t.title}
              </h3>
              <p
                className="text-text-secondary mt-2"
                style={{ fontSize: 13, lineHeight: 1.55 }}
              >
                {t.desc}
              </p>
              <span
                className="inline-flex items-center gap-1.5 mt-5"
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--color-accent)",
                }}
              >
                Use template <ArrowRight size={14} strokeWidth={2.25} />
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div
      className="rounded-[12px] border p-4 flex flex-col gap-4"
      style={{
        background: "var(--color-background-secondary)",
        borderColor: "var(--color-border-primary)",
      }}
    >
      <div className="skeleton" style={{ height: 80, width: "100%" }} />
      <div className="skeleton" style={{ height: 14, width: "60%" }} />
      <div className="flex gap-2">
        <div className="skeleton" style={{ height: 18, width: 60 }} />
        <div className="skeleton" style={{ height: 18, width: 60 }} />
      </div>
      <div className="skeleton" style={{ height: 12, width: "40%" }} />
    </div>
  );
}

const NewProjectDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState("");
  const [appType, setAppType] = useState("web-app");
  const [scaleTier, setScaleTier] = useState("starter");
  const [provider, setProvider] = useState("azure");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", {
        name: name.trim(),
        app_type: appType,
        scale_tier: scaleTier,
        selected_provider: provider,
      });
      toast.success("Architecture created", { description: data.name });
      setName("");
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not create", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border bg-bg-secondary"
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          borderRadius: 16,
        }}
      >
        <DialogHeader>
          <DialogTitle
            className="text-text-primary"
            style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em" }}
          >
            New architecture
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="new-project-form">
          <div>
            <label
              className="block mb-2 text-text-secondary"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="My side project"
              data-testid="new-project-name-input"
              className="w-full outline-none motion-micro"
              style={{
                height: 44,
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-primary)",
                borderRadius: 8,
                padding: "0 14px",
                fontSize: 14,
              }}
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label
                className="block mb-2 text-text-secondary"
                style={{ fontSize: 12, fontWeight: 500 }}
              >
                Type
              </label>
              <select
                value={appType}
                onChange={(e) => setAppType(e.target.value)}
                data-testid="new-project-apptype"
                className="w-full outline-none motion-micro"
                style={{
                  height: 40,
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                  borderRadius: 8,
                  padding: "0 10px",
                  fontSize: 13,
                }}
              >
                <option value="web-app">Web app</option>
                <option value="mobile-api">Mobile API</option>
                <option value="data-pipeline">Data pipeline</option>
                <option value="ai-service">AI service</option>
              </select>
            </div>
            <div>
              <label
                className="block mb-2 text-text-secondary"
                style={{ fontSize: 12, fontWeight: 500 }}
              >
                Scale
              </label>
              <select
                value={scaleTier}
                onChange={(e) => setScaleTier(e.target.value)}
                data-testid="new-project-scale"
                className="w-full outline-none motion-micro"
                style={{
                  height: 40,
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                  borderRadius: 8,
                  padding: "0 10px",
                  fontSize: 13,
                }}
              >
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="scale">Scale</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label
                className="block mb-2 text-text-secondary"
                style={{ fontSize: 12, fontWeight: 500 }}
              >
                Provider
              </label>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                data-testid="new-project-provider"
                className="w-full outline-none motion-micro"
                style={{
                  height: 40,
                  background: "var(--color-background-tertiary)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-primary)",
                  borderRadius: 8,
                  padding: "0 10px",
                  fontSize: 13,
                }}
              >
                <option value="azure">Azure</option>
                <option value="aws">AWS</option>
                <option value="gcp">GCP</option>
              </select>
            </div>
          </div>
          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="new-project-cancel"
              className="motion-micro"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border-primary)",
                color: "var(--color-text-secondary)",
                height: 40,
                padding: "0 16px",
                borderRadius: 8,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="new-project-submit"
              className="motion-micro"
              style={{
                background: "var(--color-accent)",
                color: "#0a0a0a",
                height: 40,
                padding: "0 16px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                border: "none",
                cursor: submitting ? "not-allowed" : "pointer",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Creating…" : "Create"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Thin adapter over the shared dialog — this used to be a full, near-duplicate
// copy of components/common/RenameDialog that had drifted from it.
const ProjectRenameDialog = ({ open, onOpenChange, project, onRenamed }) => {
  if (!project) return null;
  return (
    <SharedRenameDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Rename architecture"
      initialValue={project.name}
      onSubmit={async (name) => {
        try {
          const { data } = await api.patch(`/projects/${project.id}`, { name });
          toast.success("Renamed");
          onRenamed(data);
        } catch (e) {
          toast.error("Rename failed", {
            description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
          });
          throw e; // keeps the dialog open with the user's text intact
        }
      }}
    />
  );
};

/**
 * Mints (or reuses) the project's public read-only link. Share links are a Team
 * feature server-side, so a 402 here is expected on lower plans and is surfaced
 * as an upgrade prompt rather than a generic failure.
 */
const ShareDialog = ({ open, onOpenChange, project }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "idle", url: "", detail: "" });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !project) return;
    let cancelled = false;
    setState({ status: "loading", url: "", detail: "" });
    setCopied(false);
    api
      .post(`/projects/${project.id}/share`)
      .then(({ data }) => {
        if (cancelled) return;
        setState({
          status: "ready",
          url: `${window.location.origin}/share/${data.share_token}`,
          detail: "",
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          status: e.response?.status === 402 ? "locked" : "error",
          url: "",
          detail: formatApiErrorDetail(e.response?.data?.detail) || e.message,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [open, project]);

  if (!project) return null;

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(state.url);
      setCopied(true);
    } catch {
      // No clipboard permission — the URL is on screen to select manually.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="share-dialog"
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          borderRadius: 16,
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-text-primary" style={{ fontSize: 18, fontWeight: 600 }}>
            Share “{project.name}”
          </DialogTitle>
        </DialogHeader>

        {state.status === "loading" && (
          <p className="text-text-secondary" style={{ fontSize: 13 }}>
            Generating link…
          </p>
        )}

        {state.status === "ready" && (
          <>
            <p className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
              Anyone with this link can view the architecture. They can&apos;t edit it or see your
              account.
            </p>
            <div
              data-testid="share-dialog-url"
              className="font-mono"
              style={{
                background: "var(--color-background-tertiary)",
                border: "1px solid var(--color-border-primary)",
                borderRadius: 8,
                padding: "10px 12px",
                fontSize: 12,
                color: "var(--color-text-primary)",
                wordBreak: "break-all",
              }}
            >
              {state.url}
            </div>
          </>
        )}

        {state.status === "locked" && (
          <p className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
            {state.detail}
          </p>
        )}

        {state.status === "error" && (
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "var(--color-danger)" }}>
            {state.detail}
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="share-dialog-close"
            className="motion-micro"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Close
          </button>
          {state.status === "ready" && (
            <button
              type="button"
              onClick={copy}
              data-testid="share-dialog-copy"
              className="motion-micro inline-flex items-center gap-1.5"
              style={{
                background: "var(--color-accent)",
                color: "#0a0a0a",
                height: 40,
                padding: "0 16px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
              }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy link"}
            </button>
          )}
          {state.status === "locked" && (
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              data-testid="share-dialog-upgrade"
              className="motion-micro inline-flex items-center gap-1.5"
              style={{
                background: "var(--color-accent)",
                color: "#0a0a0a",
                height: 40,
                padding: "0 16px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
              }}
            >
              See plans <ArrowRight size={14} />
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmDeleteDialog = ({ open, onOpenChange, project, onDeleted }) => {
  const [submitting, setSubmitting] = useState(false);
  if (!project) return null;
  const submit = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success("Deleted", { description: project.name });
      onDeleted(project.id);
      onOpenChange(false);
    } catch (e) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={{
          background: "var(--color-background-secondary)",
          borderColor: "var(--color-border-primary)",
          borderRadius: 16,
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-text-primary" style={{ fontSize: 18, fontWeight: 600 }}>
            Delete this architecture?
          </DialogTitle>
        </DialogHeader>
        <p className="text-text-secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
          <span className="font-mono text-text-primary">{project.name}</span> will be permanently
          removed. This cannot be undone.
        </p>
        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="delete-cancel"
            className="motion-micro"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="delete-confirm"
            className="motion-micro"
            style={{
              background: "var(--color-danger)",
              color: "#fff",
              height: 40,
              padding: "0 16px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
            }}
          >
            {submitting ? "Deleting…" : "Delete"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


export default function Dashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null); // null = loading
  const [newOpen, setNewOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [pendingTemplate, setPendingTemplate] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error("Couldn't load architectures", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
      setProjects([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onOpenProject = (p) => {
    navigate(`/projects/${p.id}/output`);
  };

  const onArchiveProject = async (p) => {
    try {
      await api.post(`/projects/${p.id}/archive`);
      setProjects((prev) => (prev || []).filter((x) => x.id !== p.id));
      toast.success("Archived", {
        description: `${p.name} moved to archived builds — restore it from Settings.`,
      });
    } catch (e) {
      toast.error("Couldn't archive", {
        description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      });
    }
  };

  return (
    <div className="min-h-screen bg-bg-primary" data-testid="dashboard-page">
      <AppHeader />

      <main className="max-w-[1200px] mx-auto px-6 py-12">
        {projects && projects.length > 0 && (
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <p
                className="font-mono text-text-tertiary mb-3"
                style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}
              >
                Workspace
              </p>
              <h1
                className="text-text-primary"
                style={{ fontSize: 24, fontWeight: 500, letterSpacing: "-0.015em" }}
                data-testid="page-headline"
              >
                Your architectures
              </h1>
              <p
                className="text-text-secondary mt-2"
                style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 520 }}
              >
                Plan, visualize, and export cloud infrastructure before you write a line of code.
              </p>
            </div>
            <button
              onClick={() => navigate("/projects/new")}
              data-testid="new-architecture-btn"
              className="inline-flex items-center gap-2 rounded-[8px] motion-micro"
              style={{
                background: "var(--color-accent)",
                color: "#0a0a0a",
                height: 40,
                padding: "0 16px",
                fontSize: 13,
                fontWeight: 500,
                border: "none",
                cursor: "pointer",
                boxShadow:
                  "0 1px 0 rgba(255,255,255,0.1) inset, 0 8px 24px -12px rgba(91,91,214,0.6)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--color-accent-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "var(--color-accent)")}
            >
              <Plus size={16} /> New architecture
            </button>
          </div>
        )}

        {projects === null ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="loading-grid">
            {[0, 1, 2].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <WelcomeState
            onCreate={() => navigate("/projects/new")}
            // Opens the provider picker (instant, LLM-free /projects/from-template)
            // instead of routing into the chat wizard, which spent the same credit
            // to slowly re-derive an architecture the backend already has.
            onUseTemplate={(t) => setPendingTemplate(t)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onOpen={onOpenProject}
                onShare={setShareTarget}
                onRename={setRenameTarget}
                onArchive={onArchiveProject}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </main>

      <NewProjectDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        onCreated={(p) => setProjects((prev) => [p, ...(prev || [])])}
      />
      <ProjectRenameDialog
        open={!!renameTarget}
        onOpenChange={(v) => !v && setRenameTarget(null)}
        project={renameTarget}
        onRenamed={(updated) =>
          setProjects((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))
        }
      />
      <ShareDialog
        open={!!shareTarget}
        onOpenChange={(v) => !v && setShareTarget(null)}
        project={shareTarget}
      />
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        project={deleteTarget}
        onDeleted={(id) => setProjects((prev) => prev.filter((p) => p.id !== id))}
      />
      {pendingTemplate && (
        <TemplateProviderDialog
          template={pendingTemplate}
          onClose={() => setPendingTemplate(null)}
          onCreated={(project) => {
            setPendingTemplate(null);
            navigate(`/projects/${project.id}/output`);
          }}
        />
      )}
    </div>
  );
}
